import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { IMAGES, openPaymentModal } from "@/data/content";

const Hero = () => {
  const reduce = useReducedMotion();
  const [showCta, setShowCta] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowCta(true), 10000);
    return () => clearTimeout(t);
  }, []);

  const anim = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 28 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.7, delay, ease: "easeOut" },
        };

  return (
    <section id="top" data-testid="hero-section" className="relative">
      <h1 className="sr-only">
        IQMIZE — Maximize Your Mind: 7-Day Student Memory &amp; Learning System (Workbook, Parent's Guide &amp; Tools for ₹49)
      </h1>
      {/* Banner: full panoramic image visible edge-to-edge on every screen */}
      <div className="relative w-full overflow-hidden">
        <img
          src={IMAGES.heroBg}
          alt="IQMIZE — Maximize Your Mind. Learn, Think, Grow, Achieve."
          data-testid="hero-bg-image"
          className="block w-full h-auto"
          loading="eager"
        />
        {/* Floating book cover — desktop product shot over the panorama */}
        <motion.img
          {...anim(0.4)}
          src={IMAGES.hero}
          alt="IQMIZE 7-Day Student Memory & Learning System workbook"
          data-testid="hero-book-cover"
          className="pointer-events-none absolute hidden md:block right-8 lg:right-16 bottom-8 w-[180px] lg:w-[230px] rotate-3 rounded-xl border-4 border-white shadow-[0_25px_60px_rgb(0,0,0,0.35)] animate-float-slow"
        />
      </div>

      {/* CTA card — below the banner so the mountain scene stays clear */}
      <div
        data-testid="hero-cta-block"
        className={`relative z-10 -mt-8 md:-mt-14 transition-opacity duration-1000 ${
          showCta ? "opacity-100" : "opacity-0 pointer-events-none max-md:h-0 max-md:overflow-hidden max-md:mt-0 max-md:invisible"
        }`}
      >
        <div className="max-w-7xl mx-auto w-full px-5 lg:px-8 pb-6 md:pb-8 lg:pb-12">
            <motion.div
              {...anim(0.15)}
              className="rounded-3xl border border-white/70 bg-white/90 backdrop-blur-xl p-6 lg:p-8 shadow-[0_20px_50px_rgb(0,0,0,0.12)] flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 sm:justify-between"
            >
              {/* Title + breakdown — part of the delayed fade-in */}
              <div className="flex items-center gap-4 min-w-0" data-testid="hero-title-block">
                <img
                  src={IMAGES.hero}
                  alt="IQMIZE workbook cover"
                  data-testid="hero-book-cover-mobile"
                  className="w-16 shrink-0 rotate-3 rounded-lg border-2 border-white shadow-[0_10px_25px_rgb(0,0,0,0.20)] md:hidden"
                />
                <div className="min-w-0">
                  <p className="text-base lg:text-lg font-extrabold leading-snug tracking-tight text-slate-900">
                    7-Day Student Memory &amp; Learning System™
                  </p>
                  <p className="mt-1 text-xs lg:text-sm font-semibold text-slate-500">
                    Workbook + Parent's Guide + Tips &amp; Tools
                  </p>
                </div>
              </div>

              <div className="shrink-0">
                <div data-testid="hero-price-block" className="flex items-center gap-3">
                  <span className="text-lg text-slate-400 line-through">₹780</span>
                  <span className="text-4xl font-extrabold text-slate-900">₹49</span>
                  <span className="rounded-full bg-igold px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-slate-900">
                    94% off
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openPaymentModal("hero")}
                    data-testid="hero-buy-book-btn"
                    className="inline-flex items-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-bold text-white shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-brand-dark hover:shadow-xl"
                  >
                    <MessageCircle size={19} strokeWidth={2} />
                    Get the IQMIZE Book
                  </button>
                  <a
                    href="#coaching"
                    data-testid="hero-coaching-link"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-brand transition-colors duration-200"
                  >
                    Explore coaching <ArrowDown size={15} />
                  </a>
                </div>
              </div>
            </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
